import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useContext, useMemo, useState } from 'react';
import { graphqlSdk } from '../../../../Utils/graphql-client';
import useBookingTracking from '../../../../Hooks/useBookingTracking';
import useTypeSafeLocalize from '../../../../Hooks/use-type-safe-localize';
import { ErrorLocalization, LocalizationCategory } from '../../../../localizations/types';
import useBookingPrices from '../../../../Hooks/use-booking-prices';
import { StripeError } from '@stripe/stripe-js';
import { useCustomRedirectUrl } from '../../../../Hooks/use-custom-redirect-url';
import { bookingState } from '../../client-state/booking-state-context';
import { paymentReducerActionTypes } from '../../../../redusers/payment-reducer';
import useBookingMinimalShop from '../../server-data/use-booking-minimal-shop';

const useHandleBookingPayment = () => {
  const localize = useTypeSafeLocalize();
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { state, dispatch } = useContext(bookingState);
  const { data: minimalShop } = useBookingMinimalShop();
  const track = useBookingTracking();
  const { bookingPrices } = useBookingPrices();
  const isShopCountryItaly = useMemo(
    () => minimalShop?.country === 'Italy',
    [minimalShop?.country]
  );
  const [pinError, setPinError] = useState(false);

  const { redirectToCustomPage, redirectUrl } = useCustomRedirectUrl();

  const handlePayment: React.MouseEventHandler<HTMLButtonElement> = async (event) => {
    console.log('handlePayment');
    event.preventDefault();
    if (!stripe || !elements) {
      console.error('Stripe.js has not loaded', stripe, elements);
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    // Check that all required data is not null
    if (
      state.booking.id &&
      state.booking.customerId &&
      state.payment.paymentIntentClientSecret
    ) {
      setLoading(true);
      try {
        // Check https://github.com/stripe/react-stripe-js/issues/414#issuecomment-1615202528
        // for why elements.submit() is called
        const { error: validationError } = await elements.submit();
        // The Stripe Payment Element automatically displays the error
        if (validationError) return setLoading(false);

        if (isShopCountryItaly) {
          const addressElement = elements.getElement('address');

          if (!addressElement) {
            throw new Error('Address element not found');
          }

          const { value } = await addressElement.getValue();

          await graphqlSdk.booking_updateAndValidateCustomer({
            input: {
              firstname: value.firstName,
              lastname: value.lastName,
              email: state.customer.customer.email,
              phone: state.customer.customer.phone,
              personalIdentificationNumber: state.customer.customer.pin,
              address: {
                streetAddress: `${value.address.line1 || ''} ${
                  value.address.line2 || ''
                }`.trim(),
                zip: value.address.postal_code,
                city: value.address.city,
                country: value.address.country,
              },
            },
            id: state.booking.customerId,
          });
        }

        await graphqlSdk.booking_initiatePaymentCreateBooking({
          bookingId: state.booking.id,
          paymentIntentId: state.payment.paymentIntentClientSecret.replace(
            /_secret_.+/,
            ''
          ),
          clientPrice: bookingPrices.toBePaid ?? 0,
          subscribeToNewsletter: state.terms.newsletterSubscribed ?? false,
        });

        let error: StripeError | undefined | null = null;

        if (redirectToCustomPage) {
          const stripeResult = await stripe.confirmPayment({
            elements,
            confirmParams: {
              return_url: redirectUrl,
            },
            redirect: 'always',
          });

          error = stripeResult.error;
        } else {
          const stripeResult = await stripe.confirmPayment({
            elements,
            confirmParams: {
              return_url: redirectUrl,
            },
            redirect: 'if_required',
          });

          error = stripeResult.error;
        }

        if (error && error.message) {
          // This point will only be reached if there is an immediate error when
          // confirming the payment. Show error to your customer (for example, payment
          // details incomplete)
          setErrorMessage(error.message);
          setLoading(false);
        } else {
          // Your customer will be redirected to your `return_url`. For some payment
          // methods like iDEAL, your customer will be redirected to an intermediate
          // site first to authorize the payment, then redirected to the `return_url`.

          setLoading(false);
          if (!redirectToCustomPage) {
            track({ type: 'TRANSACTION_COMPLETE', product: state.booking });
          }
          dispatch({ type: paymentReducerActionTypes.UPDATE_IS_PAID, payload: true });
        }
      } catch (error: any) {
        setLoading(false);
        if (error?.response?.errors?.[0]?.extensions?.code === 'price-mismatch') {
          setErrorMessage(
            localize(LocalizationCategory.error, ErrorLocalization.PriceMismatchError)
          );
        } else if (error?.response?.errors?.[0]?.extensions?.code === 'BAD_USER_INPUT') {
          // we could make a custom error message if the customer details are rejected by the backend
          setErrorMessage(
            localize(LocalizationCategory.error, ErrorLocalization.UnknownError)
          );
        } else if (
          /^invalid-condition-/.test(error?.response?.errors?.[0]?.extensions?.code)
        ) {
          setErrorMessage(
            `${localize(
              LocalizationCategory.error,
              ErrorLocalization.PersonalDetailsInvalid
            )}: ${error?.response?.errors?.[0]?.message || ''}`
          );
          if (
            error?.response?.errors?.[0]?.extensions?.code ===
            'invalid-condition-pinValid'
          ) {
            setPinError(true);
          }
        } else {
          console.error(
            `unknown error: ${error?.response?.errors?.[0]?.extensions?.code}`
          );
          setErrorMessage(
            localize(LocalizationCategory.error, ErrorLocalization.UnknownError)
          );
        }
      }
    }
  };

  return { handlePayment, errorMessage, loading, pinError, setPinError };
};

export default useHandleBookingPayment;
